import type { VisitasDto, VisitasPlanDto } from '../../types/visitas';
import { formatDate, formatDateTime } from '../../utils/formatters';

export interface ExportVisitasPlanPdfMeta {
  vendedorLabel: string;
}

const MAX_CELL = 140;

function truncate(s: string | null | undefined, max = MAX_CELL): string {
  if (!s) return '';
  const t = String(s).trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function dirLabel(v: VisitasDto): string {
  const cod = v.codigoDireccion?.trim();
  const desc = v.descripcionDireccion?.trim();
  if (desc && cod) return `${desc} (${cod})`;
  if (desc) return desc;
  if (cod) return cod;
  return '-';
}

function clienteLabel(v: VisitasDto): string {
  if (v.nombreCliente?.trim()) {
    return `${truncate(v.nombreCliente, 80)} (${v.nroCliente})`;
  }
  return String(v.nroCliente);
}

/**
 * Genera y descarga un PDF del plan de visitas (cabecera + tabla de visitas).
 * Patrón alineado con otros informes del frontend (jsPDF + jspdf-autotable).
 */
export async function exportVisitasPlanPdf(
  plan: VisitasPlanDto,
  meta: ExportVisitasPlanPdfMeta,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const marginX = 40;
  let currentY = 44;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`Plan de visitas #${plan.idVisitaPlan}`, marginX, currentY);
  currentY += 24;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const headerLines: string[] = [
    `Fecha: ${formatDate(plan.fecha) || '-'}`,
    `Vendedor: ${meta.vendedorLabel}`,
    `Estado del plan: ${plan.estadoPlan}`,
    `Origen: ${plan.origenCreacion || '-'}`,
    `Usuario planificador: ${plan.usuarioPlanificador || '-'}`,
  ];
  if (plan.observaciones?.trim()) {
    headerLines.push(`Observaciones del plan: ${truncate(plan.observaciones, 220)}`);
  }
  headerLines.push(`Generado: ${new Date().toLocaleString('es-AR')}`);

  headerLines.forEach((line) => {
    doc.text(line, marginX, currentY);
    currentY += 14;
  });

  currentY += 10;

  const visitas = [...(plan.visitas ?? [])].sort(
    (a, b) => (a.orden - b.orden) || (a.idVisita - b.idVisita),
  );

  const head = [['Orden', 'Cliente', 'Dirección', 'Estado', 'Programada', 'Inicio', 'Fin', 'Omitida', 'Obs.']];

  const body =
    visitas.length === 0
      ? [['-', '-', '-', '-', '-', '-', '-', '-', 'Sin visitas cargadas']]
      : visitas.map((v) => [
          String(v.orden),
          truncate(clienteLabel(v), 100),
          truncate(dirLabel(v), 90),
          v.estado,
          formatDateTime(v.fechaHoraProgramada) || '-',
          formatDateTime(v.fechaHoraInicio) || '-',
          formatDateTime(v.fechaHoraFin) || '-',
          formatDateTime(v.fechaHoraOmitida) || '-',
          truncate(v.observaciones, 50),
        ]);

  autoTable(doc, {
    startY: currentY,
    head,
    body,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [25, 118, 210], fontStyle: 'bold' },
    margin: { left: marginX, right: marginX },
  });

  doc.save(`PlanVisitas_${plan.idVisitaPlan}.pdf`);
}
